import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import useAccess from "../hooks/useAccess";
import "../styles/SubNavigationBar.css";
import "../styles/AppLayout.css";

const SubNavigationBar = ({ sidebarOpen }) => {
  const navigate = useNavigate();
  const { id: paramId } = useParams();
  const wrapperRef = useRef(null);
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const loggedInId = localStorage.getItem("userId");
  const userRole = localStorage.getItem("userRole");
  const isAdmin = userRole === "1";
  const selectedUserId = isAdmin && paramId ? paramId : loggedInId;

  const [categories, setCategories] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [openMenu, setOpenMenu] = useState(null);
  const [isMobileCollapsed, setIsMobileCollapsed] = useState(false);

  const { hasPermission } = useAccess();

  const PERMISSIONS = useMemo(
    () => ({
      drivers: {
        view: hasPermission("AdminDashboard", "Driver License", "view"),
      },
      healthcards: {
        view: hasPermission("AdminDashboard", "Health Card", "view"),
      },
      passports: {
        view: hasPermission("AdminDashboard", "Passport", "view"),
      },
      fastcards: {
        view: hasPermission("AdminDashboard", "Fast Card", "view"),
      },
      workpermits: {
        view: hasPermission("AdminDashboard", "Work Permit", "view"),
      },
      workhistory: {
        view: hasPermission("AdminDashboard", "Work History", "view"),
      },
      emergency: {
        view: hasPermission("AdminDashboard", "Emergency", "view"),
      },
      vacation: {
        view: hasPermission("AdminDashboard", "Vacation", "view"),
      },
      directdeposit: {
        view: hasPermission("AdminDashboard", "Direct Deposit", "view"),
      },
      fuelcards: {
        view: hasPermission("AdminDashboard", "Fuel Card", "view"),
      },
      permissions: {
        view: hasPermission("PermissionManagerPage", "Permissions", "view"),
      },
      users: {
        view: hasPermission("AdminUser", "User", "view"),
      },
    }),
    [hasPermission]
  );

  const fetchLookups = useCallback(async () => {
    try {
      const headers = {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      };
      const [catRes, statusRes] = await Promise.all([
        axios.get(`${BASE_URL}/api/dropdowns/categories`, { headers }),
        axios.get(`${BASE_URL}/api/dropdowns/user-status`, { headers }),
      ]);
      setCategories(catRes.data || []);
      setStatuses(statusRes.data || []);
    } catch (error) {
      console.error("Failed to fetch nav lookups", error);
    }
  }, [BASE_URL]);

  useEffect(() => {
    fetchLookups();
  }, [fetchLookups]);

  useEffect(() => {
    const fetchSelectedUser = async () => {
      if (!selectedUserId) return;
      try {
        const res = await axios.get(`${BASE_URL}/api/users/${selectedUserId}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        setSelectedUser(res.data);
      } catch (error) {
        console.error("Error fetching selected user:", error);
      }
    };

    fetchSelectedUser();
  }, [BASE_URL, selectedUserId]);

  useEffect(() => {
    setOpenMenu(null);
    setIsMobileCollapsed(false);
  }, [paramId]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!wrapperRef.current?.contains(event.target)) {
        setOpenMenu(null);
      }
    };

    document.addEventListener("pointerdown", handleOutsideClick);
    return () => document.removeEventListener("pointerdown", handleOutsideClick);
  }, []);

  const toUsers = (params = {}) => {
    const sp = new URLSearchParams({ tab: "users", ...params });
    navigate(`/admin?${sp.toString()}`);
    setOpenMenu(null);
  };

  const handleNavigateToUserAll = () => {
    const associate = statuses.find(
      (status) => (status.s_TranslatedText || "").trim() === "Associate"
    );
    if (associate) {
      toUsers({ status: "Associate" });
      return;
    }
    toUsers();
  };

  const handleNavigateToAdd = () => {
    navigate("/form");
    setOpenMenu(null);
  };

  const getCategoryByName = (name) =>
    categories.find((category) => (category.c_name || "").trim() === name);

  const getCategoryNameById = (c_id) =>
    categories.find((category) => String(category.c_id) === String(c_id))?.c_name || "";

  const toCategory = (c_id) => toUsers({ filter: getCategoryNameById(c_id) });
  const toCategoryGroup = (items) => {
    const names = items
      .map(({ name }) => getCategoryByName(name)?.c_name)
      .filter(Boolean);

    if (names.length === 0) {
      toUsers();
      return;
    }

    toUsers({ filter: names.join(",") });
  };

  const goTo = (path) => {
    navigate(path);
    setOpenMenu(null);
  };

  const DRIVER_ITEMS = [
    { name: "Driver", label: "Company Driver" },
    { name: "Owner Operator", label: "Owner/Operator" },
    { name: "Independent Contractor", label: "Independent Contractor" },
    { name: "Agency Driver", label: "Agency Driver" },
    { name: "Agent", label: "Agent" },
  ];

  const NON_DRIVER_ITEMS = [
    { name: "Office Staff", label: "Office Staff" },
    { name: "Shop", label: "Shop" },
    { name: "Non Driving Owner Operator", label: "Non Driving Owner/Operator" },
  ];

  const associateItems = [
    {
      key: "all-associates",
      label: <strong>All Associates</strong>,
      onClick: handleNavigateToUserAll,
    },
    {
      key: "add-associate",
      label: <strong>Add New Associate</strong>,
      onClick: handleNavigateToAdd,
    },
    {
      key: "drivers-header",
      label: <strong>Drivers</strong>,
      className: "section-header section-trigger",
      onClick: () => toCategoryGroup(DRIVER_ITEMS),
    },
    { key: "drivers-divider", divider: true },
    ...DRIVER_ITEMS.map(({ name, label }) => {
      const category = getCategoryByName(name);
      if (!category) return null;
      return {
        key: `driver-${category.c_id}`,
        label,
        onClick: () => toCategory(category.c_id),
      };
    }).filter(Boolean),
    {
      key: "non-drivers-header",
      label: <strong>Non-Drivers</strong>,
      className: "section-header section-trigger",
      onClick: () => toCategoryGroup(NON_DRIVER_ITEMS),
    },
    { key: "non-drivers-divider", divider: true },
    ...NON_DRIVER_ITEMS.map(({ name, label }) => {
      const category = getCategoryByName(name);
      if (!category) return null;
      return {
        key: `non-driver-${category.c_id}`,
        label,
        onClick: () => toCategory(category.c_id),
      };
    }).filter(Boolean),
  ];

  const sections = [
    {
      key: "recruitment",
      title: "Recruitment",
      items: [
        { key: "descriptions", label: "Descriptions", onClick: () => goTo("/job-descriptions") },
        { key: "positions", label: "Positions", onClick: () => goTo("/positions") },
      ],
    },
    {
      key: "associate-list",
      title: "Associate List",
      items: associateItems,
    },
    {
      key: "admin-tools",
      title: "Admin Tools",
      items: [
        PERMISSIONS.drivers.view && {
          key: "driver-ids",
          label: "Driver IDs",
          icon: "truck",
          onClick: () => goTo("/admin?tab=drivers"),
        },
        PERMISSIONS.healthcards.view && {
          key: "health-cards",
          label: "Health Cards",
          icon: "card",
          onClick: () => goTo("/admin?tab=healthcards"),
        },
        PERMISSIONS.fastcards.view && {
          key: "fast-cards",
          label: "Fast Cards",
          icon: "fast",
          onClick: () => goTo("/admin?tab=fastcards"),
        },
        PERMISSIONS.passports.view && {
          key: "passports",
          label: "Passports",
          icon: "passport",
          onClick: () => goTo("/admin?tab=passports"),
        },
        PERMISSIONS.workpermits.view && {
          key: "work-permits",
          label: "Work Permits",
          icon: "permit",
          onClick: () => goTo("/admin?tab=workpermits"),
        },
        PERMISSIONS.workhistory.view && {
          key: "work-history",
          label: "Work History",
          icon: "building",
          onClick: () => goTo("/admin?tab=workhistory"),
        },
        PERMISSIONS.emergency.view && {
          key: "emergency",
          label: "Emergency Contacts",
          icon: "alert",
          onClick: () => goTo("/admin?tab=emergency"),
        },
        PERMISSIONS.vacation.view && {
          key: "vacation",
          label: "Vacation Forms",
          icon: "beach",
          onClick: () => goTo("/admin?tab=vacation"),
        },
        PERMISSIONS.directdeposit.view && {
          key: "direct-deposit",
          label: "Direct Deposit",
          icon: "bank",
          onClick: () => goTo("/admin?tab=directdeposit"),
        },
        PERMISSIONS.fuelcards.view && {
          key: "fuel-card",
          label: "Fuel Card",
          icon: "fuel",
          onClick: () => goTo("/admin?tab=fuelcards"),
        },
      ].filter(Boolean),
    },
    {
      key: "tools",
      title: "Tools",
      items: [
        { key: "reports", label: "Reports", onClick: () => goTo("/reports") },
      ],
    },
    {
      key: "administration",
      title: "Administration",
      items: [
        {
          key: "account-setting",
          label: "Account Setting",
          onClick: () => goTo("/account-setting"),
        },
        PERMISSIONS.permissions.view && {
          key: "security-centre",
          label: "Security Centre",
          onClick: () => goTo("/permissions"),
        },
        PERMISSIONS.users.view && {
          key: "users",
          label: "Users",
          onClick: () => goTo("/admin-users"),
        },
        {
          key: "groups",
          label: "Groups",
          onClick: () => goTo("/admin-groups"),
        },
      ].filter(Boolean),
    },
  ];

  const renderItems = (items) =>
    items.map((item) => {
      if (item.divider) {
        return <hr key={item.key} className="dropdown-divider" />;
      }

      return (
        <div
          key={item.key}
          className={`admin-item ${item.className || ""}`}
          onClick={item.onClick}
        >
          {item.icon && <span className={`menu-icon icon-${item.icon}`} aria-hidden="true" />}
          <span>{item.label}</span>
        </div>
      );
    });

  return (
    <div
      ref={wrapperRef}
      className={`subnav-container ${sidebarOpen ? "shifted" : ""} ${
        isMobileCollapsed ? "mobile-collapsed" : ""
      }`}
    >
      <div className="dashboard-menu-bar">
        <button
          type="button"
          className="mobile-subnav-toggle"
          onClick={() => {
            setIsMobileCollapsed((prev) => !prev);
            setOpenMenu(null);
          }}
          aria-expanded={!isMobileCollapsed}
        >
          <span>{isMobileCollapsed ? "Show Navigation" : "Hide Navigation"}</span>
          <span
            className={`mobile-subnav-toggle-arrow ${isMobileCollapsed ? "collapsed" : ""}`}
            aria-hidden="true"
          >
            ▼
          </span>
        </button>

        <div className="desktop-subnav-row desktop-only-subnav">
          {sections.map((section) => (
            <div key={section.key} className="menu-item admin-item-group">
              <div className="admin-toggle">
                {section.title} <span className="admin-caret">▼</span>
              </div>
              <div className="admin-menu">{renderItems(section.items)}</div>
            </div>
          ))}
        </div>

        <div className="mobile-subnav-accordion">
          {sections.map((section) => (
            <div key={section.key} className="menu-item mobile-menu-item">
              <button
                type="button"
                className={`admin-toggle mobile-admin-toggle ${
                  openMenu === section.key ? "open" : ""
                }`}
                onClick={() =>
                  setOpenMenu((prev) => (prev === section.key ? null : section.key))
                }
                aria-expanded={openMenu === section.key}
              >
                <span>{section.title}</span>
                <span className="admin-caret">▼</span>
              </button>
              <div
                className={`admin-menu mobile-admin-menu ${
                  openMenu === section.key ? "open" : ""
                }`}
                style={{ display: openMenu === section.key ? "block" : "none" }}
              >
                {renderItems(section.items)}
              </div>
            </div>
          ))}
        </div>

        {String(selectedUser?.re_applied) === "1" && (
          <div className="reapplied-notice-global">
            This is an old profile. The user has re-applied.
          </div>
        )}
      </div>
    </div>
  );
};

export default SubNavigationBar;
